import {
	type Announcements,
	closestCenter,
	DndContext,
	type DragEndEvent,
	type DraggableAttributes,
	type DraggableSyntheticListeners,
	KeyboardSensor,
	PointerSensor,
	type ScreenReaderInstructions,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type React from "react";
import { useMemo } from "react";

export type DraggableListRenderArgs<T> = {
	item: T;
	index: number;
	isDragging: boolean;
	handleListeners: DraggableSyntheticListeners;
	/**
	 * With `handleActivator`: spread onto the handle (`DragHandle` takes them) — its ARIA role and tab stop. Without
	 * it they sit on the whole row, as before, and this is `undefined`.
	 */
	handleAttributes: DraggableAttributes | undefined;
	/** With `handleActivator`: attach to the handle, so focus returns to it after a keyboard drop. */
	handleRef: ((element: HTMLElement | null) => void) | undefined;
};

export type DraggableListProps<T> = {
	items: T[];
	/** Return a *stable* id for each item (string). */
	getId: (item: T, index: number) => string;
	/** Called with the items reordered. */
	onChange: (next: T[]) => void;
	/** Render a row. Spread `handleListeners` (and, with `handleActivator`, `handleAttributes` + `handleRef`) onto the handle. */
	renderItem: (args: DraggableListRenderArgs<T>) => React.ReactNode;
	/** Optional: customize activation constraint */
	pointerDistance?: number; // px before drag starts
	/** Optional: custom sorting strategy (defaults to vertical list) */
	strategy?: typeof verticalListSortingStrategy;
	/**
	 * The handle, not the row, is the focusable drag control: the row gets no role / tab stop of its own (so fields
	 * inside it stay ordinary fields), and the keyboard moves one slot per arrow press
	 * (`sortableKeyboardCoordinates`). Off: the row carries dnd-kit's attributes and the keyboard moves by pixels,
	 * exactly as before.
	 */
	handleActivator?: boolean;
	/** Live-region messages and instructions for screen readers (already translated). Off: dnd-kit's English defaults. */
	accessibility?: { announcements: Announcements; screenReaderInstructions: ScreenReaderInstructions };
	/** The row element — `li` when the caller wraps the list in an `ol` / `ul`. Defaults to `div`, as before. */
	itemElement?: "div" | "li";
};

export function DraggableList<T>({
	items,
	getId,
	onChange,
	renderItem,
	pointerDistance = 6,
	strategy = verticalListSortingStrategy,
	handleActivator = false,
	accessibility,
	itemElement = "div",
}: DraggableListProps<T>) {
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: { distance: pointerDistance },
		}),
		useSensor(KeyboardSensor, handleActivator ? { coordinateGetter: sortableKeyboardCoordinates } : undefined),
	);

	const ids = useMemo(() => items.map(getId), [items, getId]);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;

		const oldIndex = ids.indexOf(String(active.id));
		const newIndex = ids.indexOf(String(over.id));
		if (oldIndex === -1 || newIndex === -1) return;

		onChange(arrayMove(items, oldIndex, newIndex));
	};

	return (
		<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} accessibility={accessibility}>
			<SortableContext items={ids} strategy={strategy}>
				{items.map((item, index) => (
					<SortableRow<T>
						key={ids[index]}
						id={ids[index]}
						item={item}
						index={index}
						renderItem={renderItem}
						handleActivator={handleActivator}
						itemElement={itemElement}
					/>
				))}
			</SortableContext>
		</DndContext>
	);
}

type SortableRowProps<T> = {
	id: string;
	item: T;
	index: number;
	renderItem: DraggableListProps<T>["renderItem"];
	handleActivator: boolean;
	itemElement: "div" | "li";
};

// Internal Sortable wrapper that provides transform/drag handle props
function SortableRow<T>({ id, item, index, renderItem, handleActivator, itemElement: Element }: SortableRowProps<T>) {
	const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });

	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		// The row in hand passes over its neighbours, never under them (gated, so existing lists are unchanged).
		...(handleActivator && isDragging && { position: "relative", zIndex: 1 }),
	};

	return (
		<Element ref={setNodeRef} style={style} {...(handleActivator ? {} : attributes)}>
			{renderItem({
				item,
				index,
				isDragging,
				handleListeners: listeners,
				handleAttributes: handleActivator ? attributes : undefined,
				handleRef: handleActivator ? setActivatorNodeRef : undefined,
			})}
		</Element>
	);
}
